import { useEffect, useMemo, useState } from 'react';
import { searchAll } from '../../services/search';
import { useLanguage } from '../../context/LanguageContext';

const useInitialQuery = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search);
  return params.get('q') || '';
};

const SearchResultsPage = () => {
  const { t } = useLanguage();
  const [query, setQuery] = useState(useInitialQuery);
  const [results, setResults] = useState({
    products: [],
    brands: [],
    categories: [],
  });
  const [loading, setLoading] = useState(false);
  const hasResults =
    results.products.length ||
    results.brands.length ||
    results.categories.length;

  const handleSearch = async (nextQuery) => {
    const trimmed = nextQuery.trim();
    if (!trimmed) {
      setResults({ products: [], brands: [], categories: [] });
      return;
    }
    setLoading(true);
    try {
      const payload = await searchAll(trimmed);
      setResults(payload);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch(query);
  }, [query]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams();
      params.set('q', trimmed);
      const nextUrl = `/search?${params.toString()}`;
      if (window.location.pathname + window.location.search !== nextUrl) {
        window.history.replaceState({}, '', nextUrl);
      }
    }
    handleSearch(trimmed);
  };

  const subtitle = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return t('search.no_query');
    return t('search.results_subtitle').replace('{query}', trimmed);
  }, [query, t]);

  const openProduct = (product) => {
    if (typeof window === 'undefined') return;
    window.location.assign(`/product/${encodeURIComponent(product.productId || product.id)}`);
  };

  const openBrand = (brand) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('brand', brand.id);
    params.set('brandName', brand.name);
    window.location.assign(`/products?${params.toString()}`);
  };

  const openCategory = (category) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('category', category.id);
    window.location.assign(`/products?${params.toString()}`);
  };

  return (
    <div className="search-page">
      <header className="search-page__header">
        <h1>{t('search.results_title')}</h1>
        <p>{subtitle}</p>
        <form className="search-page__form" onSubmit={handleSubmit}>
          <input
            type="search"
            placeholder={t('header.search_placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button type="submit" className="button">
            {t('search.submit')}
          </button>
        </form>
      </header>

      {loading ? (
        <p className="search-page__status">{t('search.loading')}</p>
      ) : !query.trim() ? (
        <p className="search-page__status">{t('search.no_query')}</p>
      ) : !hasResults ? (
        <p className="search-page__status">{t('search.no_results')}</p>
      ) : (
        <div className="search-page__sections">
          {results.products.length > 0 && (
            <section>
              <h2>{t('search.products_label')}</h2>
              <div className="search-grid">
                {results.products.map((product) => (
                  <button
                    type="button"
                    key={product.productId}
                    className="search-card"
                    onClick={() => openProduct(product)}
                  >
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : null}
                    <div>
                      <p className="search-card__title">{product.name}</p>
                      <p className="search-card__meta">{product.brand}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {results.brands.length > 0 && (
            <section>
              <h2>{t('search.brands_label')}</h2>
              <div className="search-grid">
                {results.brands.map((brand) => (
                  <button
                    type="button"
                    key={brand.id}
                    className="search-card"
                    onClick={() => openBrand(brand)}
                  >
                    {brand.logo ? (
                      <img src={brand.logo} alt={brand.name} />
                    ) : null}
                    <div>
                      <p className="search-card__title">{brand.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {results.categories.length > 0 && (
            <section>
              <h2>{t('search.categories_label')}</h2>
              <div className="search-grid">
                {results.categories.map((category) => (
                  <button
                    type="button"
                    key={category.id}
                    className="search-card"
                    onClick={() => openCategory(category)}
                  >
                    <div>
                      <p className="search-card__title">{category.name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;
